import { useAsync } from 'react-use'
import { useFetchRelayInfo } from '@/hooks/useFetchRelayInfo'
import { loadNostrUser } from '@nostr/gadgets/metadata'
import { addUsernameToIndex } from '@/services/client.service'
import type { THardcodedFeed } from '@/types/universe'

/**
 * Resolve the display name + icon for a hardcoded feed item by fetching metadata
 * for the first relay and (if the feed is anchored on a pubkey) the first pubkey.
 */
export function useResolvedHardcodedFeed(item: THardcodedFeed) {
  const firstRelay = item.relays?.[0]
  const firstPubkey = item.pubkeys?.[0]
  const { relayInfo } = useFetchRelayInfo(firstRelay ?? '')

  const { value: pubkeyProfile } = useAsync(async () => {
    if (firstPubkey && item.relays?.length) {
      const profile = await loadNostrUser(firstPubkey)
      if (profile) addUsernameToIndex(profile)
      return profile
    }
    return undefined
  }, [firstPubkey, item.relays?.length])

  const getRelayName = (url: string) => (url === firstRelay ? relayInfo?.name : undefined)
  const getRelayIcon = (url: string) => (url === firstRelay ? relayInfo?.icon : undefined)
  const getPubkeyName = (pk: string) => (pk === firstPubkey ? pubkeyProfile?.shortName : undefined)
  const getPubkeyIcon = (pk: string) => (pk === firstPubkey ? pubkeyProfile?.image : undefined)

  return {
    name: resolveFeedName(item, getRelayName, getPubkeyName),
    icon: resolveFeedIcon(item, getRelayIcon, getPubkeyIcon)
  }
}

/** Build the suffix appended to a feed's display name when there is more than one source.
 * Returns '' when the suffix is unnecessary.
 *
 * The "extras" rule is: each list contributes (length - 1) extras, because we
 * treat the first element of each list as an anchor in the count (the name comes
 * from the first relay; the first pubkey's "slot" is considered used as the
 * pubkey-side anchor for the count).
 *
 * Examples:
 *   - 1 source total → ''.
 *   - 4 relays → " (+3)"
 *   - 3 pubkeys → " (+2)"
 *   - 4 relays + 5 pubkeys → " (+3 relays and +4 pubkeys)"
 *   - 1 relay + 1 pubkey → " (+0 relays and +0 pubkeys)" — degenerate; see note.
 */
export function feedCountSuffix(feed: THardcodedFeed): string {
  const r = feed.relays?.length || 0
  const p = feed.pubkeys?.length || 0
  if (r + p <= 1) return ''
  if (r > 0 && p === 0) {
    return ` (+${r - 1})`
  }
  if (p > 0 && r === 0) {
    return ` (+${p - 1})`
  }
  // mixed
  const rExtra = r - 1
  const pExtra = p - 1
  const parts: string[] = []
  if (rExtra > 0) parts.push(`+${rExtra} relay${rExtra === 1 ? '' : 's'}`)
  if (pExtra > 0) parts.push(`+${pExtra} pubkey${pExtra === 1 ? '' : 's'}`)
  if (parts.length === 0) return '' // 1+1 → no extras — caller may render without suffix
  return ` (${parts.join(' and ')})`
}

/**
 * Given a feed object and helpers to resolve relay/pubkey metadata, return
 * the best display name. Resolution order:
 *   - explicit feed.name
 *   - if exactly one relay and no pubkeys: relay name
 *   - if no relays and exactly one pubkey: pubkey name
 *   - otherwise: first relay's name + count suffix
 * If nothing is resolvable, fall back to the first relay URL or the first pubkey (truncated).
 */
export function resolveFeedName(
  feed: THardcodedFeed,
  getRelayName: (url: string) => string | undefined,
  getPubkeyName: (pubkey: string) => string | undefined
): string {
  if (feed.name) return feed.name
  const r = feed.relays?.length || 0
  const p = feed.pubkeys?.length || 0
  if (r === 1 && p === 0) {
    return getRelayName(feed.relays![0]) ?? feed.relays![0]
  }
  if (r === 0 && p === 1) {
    return getPubkeyName(feed.pubkeys![0]) ?? feed.pubkeys![0].slice(0, 8)
  }
  // 2+ sources: use first relay's name (or first pubkey's name if no relays), with suffix
  if (r > 0) {
    const base = getRelayName(feed.relays![0]) ?? feed.relays![0]
    return base + feedCountSuffix(feed)
  }
  if (p > 0) {
    const base = getPubkeyName(feed.pubkeys![0]) ?? feed.pubkeys![0].slice(0, 8)
    return base + feedCountSuffix(feed)
  }
  return '(empty feed)'
}

/**
 * Resolve the display icon URL for a feed. Same fall-back rules as `resolveFeedName`
 * but using icon URLs.
 */
export function resolveFeedIcon(
  feed: THardcodedFeed,
  getRelayIcon: (url: string) => string | undefined,
  getPubkeyIcon: (pubkey: string) => string | undefined
): string | undefined {
  if (feed.icon) return feed.icon
  const r = feed.relays?.length || 0
  const p = feed.pubkeys?.length || 0
  if (r === 1 && p === 0) return getRelayIcon(feed.relays![0])
  if (r === 0 && p === 1) return getPubkeyIcon(feed.pubkeys![0])
  if (r > 0) return getRelayIcon(feed.relays![0])
  if (p > 0) return getPubkeyIcon(feed.pubkeys![0])
  return undefined
}
